import { Select } from 'antd';
import React from 'react';
import { SendOutlined } from '@ant-design/icons';
import { TriggerAgentRequest } from '@melio/risk-ai-agents-ts-client-config';
import { AgentExecutionType } from '@tools/ui/types/generated/graphql';
import {
  EntityTypeCol,
  ExecutionTypeCol,
  FieldError,
  FieldGroup,
  FieldInput,
  FieldLabel,
  FieldRow,
  FlexCol,
  PartnerCol,
  RequiredMark,
  StyledTriggerButton,
  TriggerSection,
} from './RiskAgentRunner.styled';

const { Option } = Select;

type AgentFormProps = {
  agentName: string | undefined;
  agentsLoading: boolean;
  availableAgents: string[];
  disabled: boolean;
  entityId: string;
  entityType: TriggerAgentRequest['entity']['type'];
  executionType: AgentExecutionType;
  isTriggering: boolean;
  orchestrationExecutionId: string;
  partnerName: string;
  reason: string;
  onAgentNameChange: (v: string) => void;
  onEntityIdChange: (v: string) => void;
  onEntityTypeChange: (v: TriggerAgentRequest['entity']['type']) => void;
  onExecutionTypeChange: (v: AgentExecutionType) => void;
  onOrchestrationExecutionIdChange: (v: string) => void;
  onPartnerNameChange: (v: string) => void;
  onReasonChange: (v: string) => void;
  onTrigger: () => void;
};

const AgentForm: React.FC<AgentFormProps> = ({
  agentName,
  agentsLoading,
  availableAgents,
  disabled,
  entityId,
  entityType,
  executionType,
  isTriggering,
  orchestrationExecutionId,
  partnerName,
  reason,
  onAgentNameChange,
  onEntityIdChange,
  onEntityTypeChange,
  onExecutionTypeChange,
  onOrchestrationExecutionIdChange,
  onPartnerNameChange,
  onReasonChange,
  onTrigger,
}) => {
  const isOrchestrationIdRequired = executionType === AgentExecutionType.ORCHESTRATION;
  const isEntityIdInvalid = !!entityId.trim() && !/^\d+$/.test(entityId.trim());
  const isSendDisabled =
    !agentName ||
    !entityId.trim() ||
    isEntityIdInvalid ||
    !reason.trim() ||
    !partnerName.trim() ||
    (isOrchestrationIdRequired && !orchestrationExecutionId.trim()) ||
    isTriggering ||
    disabled;

  return (
    <>
      <FieldGroup>
        <FieldLabel>
          Agent <RequiredMark>*</RequiredMark>
        </FieldLabel>
        <Select
          value={agentName}
          onChange={onAgentNameChange}
          disabled={disabled}
          loading={agentsLoading}
          placeholder="Select a risk agent..."
          style={{ width: '100%' }}
          optionFilterProp="children"
        >
          {availableAgents.map((name) => (
            <Option key={name} value={name}>{name}</Option>
          ))}
        </Select>
      </FieldGroup>

      <FieldRow>
        <EntityTypeCol>
          <FieldLabel>Entity Type <RequiredMark>*</RequiredMark></FieldLabel>
          <Select
            value={entityType}
            onChange={onEntityTypeChange}
            disabled={disabled}
            style={{ width: '100%' }}
          >
            <Option value="organization">Organization</Option>
            <Option value="payment">Payment</Option>
          </Select>
        </EntityTypeCol>
        <FlexCol>
          <FieldLabel>Entity ID <RequiredMark>*</RequiredMark></FieldLabel>
          <FieldInput
            value={entityId}
            onChange={(e) => onEntityIdChange(e.target.value)}
            disabled={disabled}
            placeholder="e.g. 123456"
            $error={isEntityIdInvalid}
          />
          {isEntityIdInvalid && <FieldError>Must be an integer</FieldError>}
        </FlexCol>
      </FieldRow>

      <FieldRow>
        <FlexCol>
          <FieldLabel>Reason <RequiredMark>*</RequiredMark></FieldLabel>
          <FieldInput
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            disabled={disabled}
            placeholder="e.g. manual-review"
          />
        </FlexCol>
        <ExecutionTypeCol>
          <FieldLabel>Execution Type <RequiredMark>*</RequiredMark></FieldLabel>
          <Select
            value={executionType}
            onChange={onExecutionTypeChange}
            disabled={disabled}
            style={{ width: '100%' }}
          >
            <Option value="POST_DECISION">Post-Decision</Option>
            <Option value="ORCHESTRATION">Orchestration</Option>
          </Select>
        </ExecutionTypeCol>
        <PartnerCol>
          <FieldLabel>Partner <RequiredMark>*</RequiredMark></FieldLabel>
          <FieldInput
            value={partnerName}
            onChange={(e) => onPartnerNameChange(e.target.value)}
            disabled={disabled}
            placeholder="melio"
          />
        </PartnerCol>
      </FieldRow>

      {isOrchestrationIdRequired && (
        <FieldGroup>
          <FieldLabel>Orchestration Execution ID <RequiredMark>*</RequiredMark></FieldLabel>
          <FieldInput
            value={orchestrationExecutionId}
            onChange={(e) => onOrchestrationExecutionIdChange(e.target.value)}
            disabled={disabled}
            placeholder="550e8400-e29b-41d4-a716-446655440000"
          />
        </FieldGroup>
      )}

      <TriggerSection>
        <StyledTriggerButton
          $active={!isSendDisabled}
          icon={<SendOutlined onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />}
          disabled={isSendDisabled}
          onClick={onTrigger}
          loading={isTriggering}
        >
          Trigger Agent
        </StyledTriggerButton>
      </TriggerSection>
    </>
  );
};

export { AgentForm };
